<template>
	<div class="restaurant-section" :class="{ transparent: props.transparent }">
		<div class="d-flex justify-content-between align-items-center">
			<label class="form-label">Restaurant Name</label>
			<div>
				<div v-if="!isOnline" class="offline-indicator">
					<i class="bi bi-wifi-off"></i>
					<span class="ms-1">Offline</span>
				</div>
				<button
					v-if="isOnline"
					class="btn btn-sm btn-outline-orange refresh-btn m-2"
					@click="getCurrentLocation"
					:disabled="isLoading"
					title="Refresh restaurant list">
					<i
						class="bi"
						:class="
							isLoading ? 'bi-hourglass-split spin' : 'bi-arrow-clockwise'
						"></i>
					<span class="ms-1">Refresh List</span>
				</button>
			</div>
		</div>
		<!-- <div class="my-2" v-text="currentCoordsText"></div> -->
		<div class="dropdown-container">
			<div class="input-wrapper">
				<input
					type="text"
					class="form-control modern-input"
					v-model="restaurantName"
					placeholder="Type or select your restaurant"
					@input="handleInput"
					@click="toggleDropdown(true)"
					:class="{ loading: isLoading, offline: !isOnline }" />
				<span class="dropdown-arrow" @click.stop="toggleDropdownClick">
					<i
						class="bi"
						:class="[isDropdownOpen ? 'bi-chevron-up' : 'bi-chevron-down']"></i>
				</span>
				<button
					class="clear-btn"
					type="button"
					@click="clearRestaurant"
					v-if="restaurantName">
					<i class="bi bi-x-lg"></i>
				</button>
				<div class="loading-indicator" v-if="isLoading">
					<i class="bi bi-hourglass-split spin"></i>
				</div>
				<div class="offline-indicator-input" v-if="!isOnline">
					<i class="bi bi-wifi-off"></i>
				</div>
			</div>
			<ul
				class="dropdown-menu modern-dropdown"
				:class="{ show: isDropdownOpen }"
				@mousedown.prevent>
				<li v-if="!isOnline" class="offline-item">
					<i class="bi bi-wifi-off"></i>
					<span>Cannot fetch restaurants while offline</span>
				</li>
				<li v-else-if="isLoading" class="loading-item">
					<i class="bi bi-hourglass-split spin"></i>
					<span>Loading restaurants...</span>
				</li>
				<li
					v-else
					v-for="(result, index) in filteredResults"
					:key="index"
					@mousedown="selectRestaurant(result)"
					:class="{ selected: result.name === restaurantName }">
					<i class="bi bi-geo-alt"></i>
					<span class="restaurant-name">{{ result.name }}</span>
					<span class="distance">{{ result.distance }} miles</span>
					<i v-if="result.name === restaurantName" class="bi bi-check"></i>
				</li>
				<li
					v-if="isOnline && !isLoading && !filteredResults.length"
					class="no-results">
					No results found.
				</li>
			</ul>
		</div>
	</div>
</template>

<script setup>
	import { ref, computed, watch } from "vue"
	import { Geolocation } from "@capacitor/geolocation"
	import { isOnline } from "@/utils/networkBus"

	const props = defineProps({
		modelValue: {
			type: String,
			default: "",
		},
		initialRestaurantName: {
			type: String,
			default: "",
		},
		transparent: {
			type: Boolean,
			default: false,
		},
	})

	const emit = defineEmits(["update:modelValue", "customRestaurant"])

	const restaurantName = ref(
		props.initialRestaurantName || props.modelValue || ""
	)
	const searchResults = ref([])
	const isDropdownOpen = ref(false)
	const isLoading = ref(false)
	const hasAttemptedFetch = ref(false)
	let isCustomRestaurant = ref(false)
	let currentUserPosition = { latitude: null, longitude: null }
	const currentCoordsText = ref("")

	// Watch for changes to modelValue from parent
	watch(
		() => props.modelValue,
		(newValue) => {
			if (newValue !== restaurantName.value) {
				restaurantName.value = newValue
			}
		},
		{ immediate: true }
	)

	// Watch for changes to initialRestaurantName from parent
	watch(
		() => props.initialRestaurantName,
		(newValue) => {
			if (newValue && !restaurantName.value) {
				restaurantName.value = newValue
			}
		},
		{ immediate: true }
	)

	const filteredResults = computed(() => {
		if (!restaurantName.value.trim()) return searchResults.value
		return searchResults.value.filter((r) =>
			r.name.toLowerCase().includes(restaurantName.value.toLowerCase())
		)
	})

	const getCurrentLocation = async () => {
		if (!isOnline.value) return

		isLoading.value = true
		try {
			currentCoordsText.value = "Fetching coords"
			const coordinates = await Geolocation.getCurrentPosition({
				enableHighAccuracy: true,
				timeout: 30 * 1000,
				maximumAge: 5 * 1000, //can read cache version of 5 seconds old
			})
			if (coordinates) {
				currentUserPosition.latitude = coordinates.coords.latitude
				currentUserPosition.longitude = coordinates.coords.longitude
				fetchNearbyPlaces()
				currentCoordsText.value = `${coordinates.coords.latitude}, ${coordinates.coords.longitude}`
			} else {
				currentCoordsText.value = "Unable to retrieve your location"
			}
		} catch (e) {
			console.log(e)
			isLoading.value = false
			currentCoordsText.value = e.message
		}
	}

	let fetchTimeout = null
	const fetchNearbyPlaces = async () => {
		if (!isOnline.value) return

		if (fetchTimeout) clearTimeout(fetchTimeout)
		isLoading.value = true
		hasAttemptedFetch.value = true

		fetchTimeout = setTimeout(async () => {
			const params = new URLSearchParams({
				latitude: currentUserPosition.latitude,
				longitude: currentUserPosition.longitude,
			})
			const url = `/api/restaurant_name?${params.toString()}`

			try {
				const response = await fetch(url)
				const data = await response.json()
				if (data.error) console.error("API Error:", data.error)
				else {
					searchResults.value = data.results

					if (!restaurantName.value) {
						const autoSelected = searchResults.value.find(
							(r) => +r.distance <= 0.04
						)
						if (autoSelected) {
							restaurantName.value = autoSelected.name
							isCustomRestaurant.value = false
							updateModelValue()
						}
						isDropdownOpen.value = true
					}
				}
			} catch (error) {
				console.error("Failed to search nearby places:", error)
			} finally {
				isLoading.value = false
				fetchTimeout = null
			}
		}, 100)
	}

	const selectRestaurant = (place) => {
		restaurantName.value = place.name
		isCustomRestaurant.value = false
		isDropdownOpen.value = false

		emit("customRestaurant", {
			isCustom: false,
			name: place.name,
			position: null,
		})
		updateModelValue()
	}

	const clearRestaurant = () => {
		restaurantName.value = ""
		isCustomRestaurant.value = false

		emit("customRestaurant", {
			isCustom: false,
			name: "",
			position: null,
		})
		updateModelValue()
	}

	const updateModelValue = () => {
		emit("update:modelValue", restaurantName.value)
	}

	const handleInput = () => {
		if (!isDropdownOpen.value) {
			toggleDropdown(true)
		}

		// Check if the restaurant is custom
		const matchingResult = searchResults.value.find(
			(r) => r.name.toLowerCase() === restaurantName.value.toLowerCase()
		)
		isCustomRestaurant.value =
			!matchingResult && restaurantName.value.trim() !== ""

		emit("customRestaurant", {
			isCustom: isCustomRestaurant.value,
			name: restaurantName.value,
			position: currentUserPosition,
		})
		updateModelValue()
	}

	const toggleDropdownClick = () => {
		toggleDropdown(!isDropdownOpen.value)
	}

	const toggleDropdown = (open) => {
		isDropdownOpen.value = open
	}

	// Initialize component
	if (!hasAttemptedFetch.value && isOnline.value) {
		getCurrentLocation()
	}

	// Watch for online status changes
	watch(isOnline, (newValue, oldValue) => {
		// If we're coming back online and we haven't fetched restaurants yet
		if (newValue && !oldValue && !hasAttemptedFetch.value) {
			getCurrentLocation()
		}
	})
</script>

<style lang="less" scoped>
	.dropdown-container {
		position: relative;
	}

	.input-wrapper {
		position: relative;
	}

	.modern-input {
		border-radius: 8px;
		border: 2px solid #e0e0e0;
		padding: 12px 70px 12px 15px;
		font-size: 16px;
		width: 100%;
		background-color: #fff;
		transition: border-color 0.3s ease, box-shadow 0.3s ease;
		&:focus {
			border-color: #ff6600; /* Orange accent */
			box-shadow: 0 0 5px rgba(255, 102, 0, 0.3);
			outline: none;
		}
		&::placeholder {
			color: darkgrey;
		}
	}

	/* Transparent mode overrides */
	.restaurant-section.transparent {
		position: relative;
		z-index: 3000;

		.modern-input {
			background-color: transparent !important;
			color: #ffffff !important;
			border-color: rgba(255, 255, 255, 0.25);
		}

		.dropdown-arrow,
		.clear-btn,
		.loading-indicator,
		.offline-indicator-input {
			color: #ffffff;
		}

		.modern-dropdown {
			background-color: rgba(0, 0, 0, 0.75);
			border-color: rgba(255, 255, 255, 0.25);
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);

			li {
				color: #ffffff;

				&:hover {
					background-color: rgba(255, 255, 255, 0.08);
				}

				&.selected {
					background-color: rgba(255, 255, 255, 0.16);
				}

				.bi-geo-alt {
					color: #ffb380; /* softer orange on dark */
				}

				.restaurant-name {
					color: #ffffff;
				}

				.distance {
					color: #dddddd;
				}

				.bi-check {
					color: #22c55e;
				}
			}

			.no-results,
			.loading-item,
			.offline-item {
				color: #ffffff;
			}
		}
	}

	.dropdown-arrow {
		position: absolute;
		right: 40px;
		top: 50%;
		transform: translateY(-50%);
		color: #666;
		cursor: pointer;
		padding: 10px;

		&:hover {
			color: #ff6600;
		}
	}

	.clear-btn {
		position: absolute;
		right: 10px;
		top: 50%;
		transform: translateY(-50%);
		background: transparent;
		border: none;
		color: #666;
		font-size: 20px;
		cursor: pointer;
		&:hover {
			color: #ff6600; /* Orange accent */
		}
	}

	.modern-dropdown {
		position: absolute;
		top: 100%;
		left: 0;
		right: 0;
		margin-top: 5px;
		border-radius: 8px;
		border: 1px solid #ddd;
		background-color: #fff;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
		max-height: 300px;
		overflow-y: auto;
		list-style: none;
		padding: 0;
		opacity: 0;
		visibility: hidden;
		transform: translateY(-10px);
		transition: opacity 0.3s ease, transform 0.3s ease, visibility 0s 0.3s;
		z-index: 7000;

		&.show {
			opacity: 1;
			visibility: visible;
			transform: translateY(0);
			transition: opacity 0.3s ease, transform 0.3s ease;
		}

		li {
			display: flex;
			align-items: center;
			padding: 12px 15px;
			cursor: pointer;
			transition: background-color 0.2s ease;

			&:hover {
				background-color: #fff3e6; /* Lighter orange for hover */
			}

			&.selected {
				background-color: #ffe6cc; /* Light orange for selection */
			}

			.bi-geo-alt {
				color: #ff6600; /* Orange accent */
				margin-right: 10px;
			}

			.restaurant-name {
				flex: 1;
				font-size: 16px;
				color: #333;
			}

			.distance {
				font-size: 14px;
				color: #888;
				margin-left: auto;
			}

			.bi-check {
				color: #28a745; /* Green checkmark */
				margin-left: 10px;
			}
		}

		.no-results {
			padding: 12px 15px;
			color: #888;
			text-align: center;
		}
	}

	.loading-indicator {
		position: absolute;
		right: 70px;
		top: 50%;
		transform: translateY(-50%);
		color: #ff6600;
	}

	.loading-item {
		display: flex;
		align-items: center;
		padding: 12px 15px;
		color: #ff6600;

		i {
			margin-right: 10px;
		}
	}

	.spin {
		animation: spin 1.5s linear infinite;
	}

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	.refresh-btn {
		padding: 0.25rem 0.5rem;
		font-size: 0.875rem;
		border-radius: 4px;
		display: flex;
		align-items: center;
		color: #ff6600;
		border-color: #ff6600;

		&:hover {
			color: #fff;
			background-color: #ff6600;
			border-color: #ff6600;
		}

		&:disabled {
			opacity: 0.6;
			color: #ff6600;
			border-color: #ff6600;
			background-color: transparent;
		}
	}

	.btn-outline-orange {
		color: #ff6600;
		border-color: #ff6600;

		&:hover {
			color: #fff;
			background-color: #ff6600;
		}

		&:disabled {
			color: #ff6600;
		}
	}

	.modern-input.loading {
		background-image: linear-gradient(
			90deg,
			#fff,
			#ffe0b2 40%,
			#fff8f0 60%,
			#fff
		);
		background-size: 300% 100%;
		animation: loading-pulse 1.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
		transition: background 0.3s;
	}

	.restaurant-section.transparent .modern-input.loading {
		background-image: linear-gradient(
			90deg,
			rgba(255, 255, 255, 0.1) 0%,
			rgba(255, 166, 77, 0.18) 40%,
			rgba(255, 255, 255, 0.1) 60%,
			rgba(255, 255, 255, 0.05) 100%
		);
		background-size: 300% 100%;
		animation: loading-pulse 1.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
		transition: background 0.3s;
		box-shadow: 0 0 8px 0 rgba(255, 166, 77, 0.15);
	}

	@keyframes loading-pulse {
		0% {
			background-position: 0% 50%;
		}
		50% {
			background-position: 100% 50%;
		}
		100% {
			background-position: 0% 50%;
		}
	}

	.offline-indicator {
		display: flex;
		align-items: center;
		color: #dc3545;
		font-size: 0.875rem;
		margin-right: 0.5rem;

		i {
			margin-right: 0.25rem;
		}
	}

	.offline-indicator-input {
		position: absolute;
		right: 70px;
		top: 50%;
		transform: translateY(-50%);
		color: #dc3545;
	}

	.offline-item {
		display: flex;
		align-items: center;
		padding: 12px 15px;
		color: #dc3545;

		i {
			margin-right: 10px;
		}
	}

	.modern-input.offline {
		background-color: #f8f9fa;
		border-color: #ced4da;
		color: #6c757d;
	}
</style>
